//@ts-check
if (!customElements.get('c-engraving-panel')) {
  /** @type {Record<string, { diptyquePx: number, desmondPx: number }>} */
  const VIEWER_FONT_SIZES_BY_SKU_3D = {
    engraving_edp_75: { diptyquePx: 100, desmondPx: 190 },
    engraving_190g: { diptyquePx: 100, desmondPx: 190 },
  };

  const DEFAULT_VIEWER_FONT_SIZES = {
    diptyquePx: 100,
    desmondPx: 190,
  };

  class CEngravingPanel extends HTMLElement {
    constructor() {
      super();
      /** @type {string[]} */
      this.forbiddenWords = [];
      /** @type {string[]} */
      this.allowedSymbols = [];
      /** @type {string} */
      this.currentPosition = '';
      /** @type {object} */
      this.possiblePositions = {
        back: '180,1',
        side: '90,1',
      };
      /** @type {{ setView: (azimuth: number | null, polar: number | null, dolly: number | null, duration?: number, options?: object) => void } | null} */
      this._viewer360 = null;
      /** @type {{ setView: (azimuth: number | null, polar: number | null, dolly: number | null, duration?: number, options?: object) => void } | null} */
      this._viewer3D = null;
      /** @type {boolean} */
      this._viewersListenerAttached = false;
      /** @type {boolean} */
      this._pendingIntroSpin = false;
      /** @type {number} */
      this._introSpinDurationMs = 2500;
      /** @type {object} */
      this._introSpinOptions = {
        preserveTurns: true,
      };
      /** @type {ReturnType<typeof setTimeout> | null} */
      this._positionTurnTimeoutId = null;
      /** @type {number} */
      this._positionTurnDebounceMs = 450;
      /** @type {boolean} */
      this._positionTurnCooldown = false;
    }

    connectedCallback() {
      this.initElements();
      this.addEventListener('input', (e) => this.handleInputChange(e));
      this.addEventListener('change', (e) => this.handleInputChange(e));

      // Click handler for everything (Tabs, Next Buttons, Submit, Close)
      this.addEventListener('click', (e) => this.handleClick(e));

      // Handle focus/blur on tab inputs to highlight labels
      this.addEventListener('focusin', (e) => {
        const target = e.target;
        if (target && target.hasAttribute('data-tab-id')) {
          this.updateLabelFocus(target, true);
        }
      });
      this.addEventListener('focusout', (e) => {
        const target = e.target;
        if (target && target.hasAttribute('data-tab-id')) {
          this.updateLabelFocus(target, false);
        }
      });

      // External trigger buttons
      // We listen globally for clicks on elements that target this specific panel
      document.addEventListener('click', (e) => {
        const trigger = e.target.closest(
          `[aria-haspopup="dialog"][aria-controls="${this.id}"]`,
        );
        if (trigger) {
          this.open();
        }
      });

      // Auto-close on successful cart addition
      this.addEventListener('cart:add-success', () => this.close());

      this.checkHash();
      window.addEventListener('hashchange', () => this.checkHash());
      this.initViewerListeners();

      this.validateAll();
      this.syncSelectedFontSizeInputs();
    }

    initViewerListeners() {
      if (this._viewersListenerAttached) return;
      this._viewersListenerAttached = true;

      window.addEventListener('POWERViewersLoaded', (event) => {
        if (!this.isConnected || !this.querySelector('power-viewer-360-3d')) {
          return;
        }

        const detail = /** @type {CustomEvent} */ (event).detail;
        this._viewer360 = detail?.viewer360 ?? null;
        this._viewer3D = detail?.viewer3D ?? null;

        if (this._pendingIntroSpin && this.classList.contains('is-open')) {
          setTimeout(() => {
            this.playIntroSpin();
          }, 500);
        }
      });
    }

    /**
     * @param {string} position
     * @returns {{ key: string, textArea: string, focusView: string } | null}
     */
    getPositionConfig(position) {
      if (position === 'Back') {
        return {
          key: 'back',
          textArea: 'back',
          focusView: this.possiblePositions.back,
        };
      }

      if (position === 'Side') {
        return {
          key: 'side',
          textArea: 'side',
          focusView: this.possiblePositions.side,
        };
      }

      return null;
    }

    getSelectedEngravingPosition() {
      /** @type {HTMLInputElement | null} */
      const positionInput = this.querySelector(
        'input[name*="[__Engraving-position]"]:checked',
      );
      return positionInput?.value ?? '';
    }

    /**
     * @returns {HTMLInputElement | null}
     */
    getActivePositionRadio() {
      return this.querySelector('input[data-template-nbr]:checked');
    }

    /**
     * @returns {string}
     */
    getActiveTemplateNbr() {
      const positionRadio = this.getActivePositionRadio();
      return positionRadio?.dataset.templateNbr ?? '';
    }

    /**
     * @param {string} templateNbr
     * @returns {HTMLElement | null}
     */
    getTemplateContentEl(templateNbr) {
      if (!templateNbr) return null;
      return this.querySelector(`.engraving-step__content-${templateNbr}`);
    }

    /**
     * @param {string} templateNbr
     * @returns {string[]}
     */
    getTextLinesForTemplate(templateNbr) {
      const content = this.getTemplateContentEl(templateNbr);
      if (!content) return ['', ''];

      return Array.from(
        content.querySelectorAll('[data-engraving-input]'),
      ).map((input) => input.value);
    }

    /**
     * @param {HTMLElement} input
     * @returns {string}
     */
    getTemplateNbrForInput(input) {
      const content = input.closest('[class*="engraving-step__content-"]');
      if (!content) return '';

      const match = content.className.match(/engraving-step__content-(\d+)/);
      return match?.[1] ?? '';
    }

    /**
     * @param {string} sku3d
     * @returns {{ diptyquePx: number, desmondPx: number }}
     */
    getViewerFontSizesForSku(sku3d) {
      return VIEWER_FONT_SIZES_BY_SKU_3D[sku3d] ?? DEFAULT_VIEWER_FONT_SIZES;
    }

    /**
     * @returns {string}
     */
    getActiveSku3d() {
      const positionRadio = this.getActivePositionRadio();
      if (positionRadio?.dataset.sku3d) {
        return positionRadio.dataset.sku3d;
      }

      const viewer = this.querySelector('power-viewer-360-3d');
      return viewer?.getAttribute('sku-3d') ?? '';
    }

    /**
     * @returns {{ sku3d: string, diptyquePx: number, desmondPx: number }}
     */
    getActiveTemplateViewerConfig() {
      const sku3d = this.getActiveSku3d();

      return {
        sku3d,
        ...this.getViewerFontSizesForSku(sku3d),
      };
    }

    /**
     * @param {HTMLElement} input
     */
    isInputForActiveTemplate(input) {
      if (!input.matches('[data-engraving-input]')) return false;
      return this.getTemplateNbrForInput(input) === this.getActiveTemplateNbr();
    }

    /**
     * @param {string} position
     */
    syncViewerTextForPosition(position) {
      const config = this.getPositionConfig(position);
      const viewer = this.querySelector('power-viewer-360-3d');
      if (!viewer || !config) return;

      const positionRadio = Array.from(
        this.querySelectorAll('input[data-template-nbr]'),
      ).find((radio) => radio.value === position);
      const templateNbr = positionRadio?.dataset.templateNbr ?? '';
      const lines = this.getTextLinesForTemplate(templateNbr);

      viewer.setAttribute('text-area', config.textArea);

      const text = lines.join('\n');
      viewer.setAttribute('text', text === '' ? ' ' : text);
    }

    syncViewerTextFromActivePosition() {
      const position = this.getSelectedEngravingPosition();
      if (!position) return;
      this.syncViewerTextForPosition(position);
    }

    /**
     * @param {string} focusView
     * @returns {{ azimuth: number, polar: number, dolly: number | null }}
     */
    parseFocusView(focusView) {
      const [azimuth = '0', polar = '0', dolly = ''] = focusView
        .split(',')
        .map((value) => value.trim());

      return {
        azimuth: parseFloat(azimuth),
        polar: parseFloat(polar),
        dolly: dolly === '' ? null : parseFloat(dolly),
      };
    }

    /**
     * @param {number} degrees
     */
    normalizeAngle(degrees) {
      return ((degrees % 360) + 360) % 360;
    }

    /**
     * @param {number} current
     * @param {number} target
     * @param {number} tolerance
     */
    isAngleNear(current, target, tolerance = 12) {
      const delta = Math.abs(
        this.normalizeAngle(current) - this.normalizeAngle(target),
      );
      return delta <= tolerance || delta >= 360 - tolerance;
    }

    /**
     * @param {HTMLElement} viewer
     */
    getViewerOrbit(viewer) {
      const viewer360 = viewer.querySelector('power-viewer-360');
      if (!viewer360) return null;

      const azimuth = parseFloat(
        viewer360.getAttribute('data-orbit-azimuth') ?? '',
      );
      const polar = parseFloat(viewer360.getAttribute('data-orbit-polar') ?? '');
      const dolly = parseFloat(viewer360.getAttribute('data-orbit-dolly') ?? '');

      if (Number.isNaN(azimuth)) return null;

      return {
        azimuth,
        polar: Number.isNaN(polar) ? null : polar,
        dolly: Number.isNaN(dolly) ? null : dolly,
      };
    }

    /**
     * Compares the viewer camera to the target engraving position.
     * text-area defaults to "back" in markup while the camera can still face front —
     * use focus-view when set, otherwise fall back to live orbit angles.
     *
     * @param {HTMLElement | null} viewer
     * @param {string} position
     */
    isViewerAtPosition(viewer, position) {
      const config = this.getPositionConfig(position);
      if (!viewer || !config) return false;

      const currentFocusView = viewer.getAttribute('focus-view')?.trim() ?? '';
      if (currentFocusView !== '') {
        return currentFocusView === config.focusView;
      }

      const target = this.parseFocusView(config.focusView);
      const orbit = this.getViewerOrbit(viewer);

      if (!orbit) return false;

      if (!this.isAngleNear(orbit.azimuth, target.azimuth)) {
        return false;
      }

      if (orbit.polar !== null && !Number.isNaN(target.polar)) {
        return Math.abs(orbit.polar - target.polar) <= 12;
      }

      return true;
    }

    playIntroSpin() {
      if (!this._viewer360?.setView) return;

      this._pendingIntroSpin = false;
      this._viewer360.setView(359.9, 0, 1, this._introSpinDurationMs, this._introSpinOptions);
    }

    checkHash() {
      if (
        window.location.hash === '#engraving' &&
        this.dataset.isProductPage === 'true'
      ) {
        this.open();
      }
    }

    /**
     * Updates the focus state of a label based on the focus state of its associated input.
     * @param {HTMLInputElement} input - The input element that has gained or lost focus.
     * @param {boolean} isFocused - Whether the input is currently focused.
     */
    updateLabelFocus(input, isFocused) {
      const labels = document.querySelectorAll(`label[for="${input.id}"]`);
      labels.forEach((label) => {
        label.classList.toggle('is-focused', isFocused);
      });
    }

    /**
     * Focuses the first focusable element in the specified step.
     * @param {number} stepIndex - The index of the step to focus.
     */
    focusFirstInStep(stepIndex) {
      const stepClasses = [
        '.engraving-step--position',
        '.engraving-step--text',
        '.engraving-step--fonts',
        '.engraving-step--colors',
      ];
      const currentStepClass = stepClasses[stepIndex];
      if (!currentStepClass) return;

      const stepContent = this.querySelector(currentStepClass);
      if (!stepContent) return;

      // Small delay to ensure the step is visible (due to CSS transitions or display changes)
      setTimeout(() => {
        const focusable = Array.from(
          stepContent.querySelectorAll(
            'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
          ),
        ).filter((el) => {
          const style = window.getComputedStyle(el);
          return (
            style.display !== 'none' &&
            style.visibility !== 'hidden' &&
            (el.offsetWidth > 0 ||
              (el.tagName === 'INPUT' && el.type === 'radio'))
          );
        });

        if (focusable.length > 0) {
          focusable[0].focus();
        }
      }, 100);
    }

    initElements() {
      this.form = this.querySelector('form');
      this.tabInputs = Array.from(
        this.querySelectorAll('.engraving-tabs__item input[data-tab-id]'),
      );
      this.footerPanels = Array.from(this.querySelectorAll('.bottom-panel'));
      this.currentTabIndex = this.tabInputs.findIndex((input) => input.checked);
      if (this.currentTabIndex === -1) this.currentTabIndex = 0;
      this.desktopSubmitButton = this.querySelector(
        '.engraving-panel__footer-desktop button[type="submit"]',
      );
      this.closeButtons = Array.from(
        this.querySelectorAll(
          '[aria-label="close engraving"], [aria-label="Close"]',
        ),
      );

      // Initialize validation lists from data attributes
      this.forbiddenWords = this.parseList(this.dataset.listForbiddenWords);
      this.allowedSymbols = this.parseList(this.dataset.listAllowedSymbol);

      // If lists are empty, use reasonable defaults or fallback to what was previously static
      if (this.forbiddenWords.length === 0) {
        this.forbiddenWords = [];
      }
    }

    /**
     * Parses a string value into an array of strings.
     * @param {string} value - The value to parse.
     */
    parseList(value) {
      if (!value || value === '[]' || value === '') return [];

      // If it starts with [, it's likely a JSON array
      if (value.trim().startsWith('[')) {
        try {
          const parsed = JSON.parse(value);
          return Array.isArray(parsed) ? parsed : [parsed];
        } catch (e) {
          // Fallback if JSON parsing fails
        }
      }

      // Default: split by comma-separated string as clarified by user
      return value
        .split(',')
        .map((s) => s.trim())
        .filter((s) => s !== '');
    }

    open() {
      this.lastFocusedElement = document.activeElement;
      this.classList.add('is-open');
      this.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';

      this.trapFocus();

      // Ensure UI is in sync with current input states
      this.validateAll();
      this.syncDefaultValues();
      this._pendingIntroSpin = true;
      this.playIntroSpin();

      // Focus the first focusable element
      const focusableElements = this.getFocusableElements();
      if (focusableElements.length > 0) {
        focusableElements[0].focus();
      }
    }

    clearPositionTurnDebounce() {
      if (this._positionTurnTimeoutId !== null) {
        clearTimeout(this._positionTurnTimeoutId);
        this._positionTurnTimeoutId = null;
      }
    }

    resetPositionTurnCooldown() {
      this.clearPositionTurnDebounce();
      this._positionTurnCooldown = false;
    }

    /**
     * First keystroke triggers the turn immediately; further keystrokes within
     * 450ms are ignored until the user pauses typing.
     */
    scheduleViewerPositionTurn() {
      if (!this._positionTurnCooldown) {
        this._positionTurnCooldown = true;
        this.applyViewerPositionIfNeeded();
      }

      this.clearPositionTurnDebounce();
      this._positionTurnTimeoutId = window.setTimeout(() => {
        this._positionTurnTimeoutId = null;
        this._positionTurnCooldown = false;
      }, this._positionTurnDebounceMs);
    }

    applyViewerPositionIfNeeded() {
      const viewer = this.querySelector('power-viewer-360-3d');
      const selectedPosition =
        this.getSelectedEngravingPosition() || this.currentPosition;

      if (
        viewer &&
        selectedPosition &&
        !this.isViewerAtPosition(viewer, selectedPosition)
      ) {
        this.update3DViewerPosition(selectedPosition);
      }
    }

    close() {
      this.classList.remove('is-open');
      this.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
      this._pendingIntroSpin = false;
      this.resetPositionTurnCooldown();
      this.resetIntroSpin();
      this.releaseFocus();

      if (this.lastFocusedElement) {
        this.lastFocusedElement.focus();
      }
    }

    resetIntroSpin() {
      this._viewer360?.setView(0, 0, 1);
    }

    getFocusableElements() {
      const focusableSelectors = [
        'button:not([disabled])',
        '[href]',
        'input:not([disabled]):not([type="hidden"])',
        'select:not([disabled])',
        'textarea:not([disabled])',
        '[tabindex]:not([tabindex="-1"])',
      ].join(',');

      const allElements = Array.from(this.querySelectorAll(focusableSelectors));
      const radioGroupsOccurred = new Set();

      return allElements.filter((el) => {
        const style = window.getComputedStyle(el);
        if (style.display === 'none' || style.visibility === 'hidden')
          return false;

        // Support for visually hidden but focusable elements (the 1x1 scale/clip technique)
        const isVisuallyHiddenInput =
          el.tagName === 'INPUT' &&
          (el.type === 'radio' || el.type === 'checkbox');

        if (
          !isVisuallyHiddenInput &&
          el.offsetWidth === 0 &&
          el.offsetHeight === 0
        )
          return false;

        // Radio group logic: only one radio per group should be in the tab order
        if (el.tagName === 'INPUT' && el.type === 'radio') {
          if (radioGroupsOccurred.has(el.name)) return false;
          radioGroupsOccurred.add(el.name);

          const group = Array.from(
            this.querySelectorAll(`input[name="${CSS.escape(el.name)}"]`),
          );
          const checked = group.find((r) => r.checked);
          return checked ? el === checked : el === group[0];
        }

        return true;
      });
    }

    trapFocus() {
      this._keydownHandler = (e) => {
        if (e.key === 'Escape') {
          this.close();
          return;
        }

        if (e.key === 'Enter') {
          const target = e.target;
          if (
            target instanceof HTMLInputElement &&
            target.matches('[data-engraving-input]')
          ) {
            e.preventDefault();
            target.blur();
            return;
          }
        }

        if (e.key === 'Tab') {
          const focusableElements = this.getFocusableElements();
          const firstElement = focusableElements[0];
          const lastElement = focusableElements[focusableElements.length - 1];

          if (e.shiftKey) {
            if (document.activeElement === firstElement) {
              e.preventDefault();
              lastElement.focus();
            }
          } else {
            if (document.activeElement === lastElement) {
              e.preventDefault();
              firstElement.focus();
            }
          }
        }
      };
      this.addEventListener('keydown', this._keydownHandler);
    }

    releaseFocus() {
      if (this._keydownHandler) {
        this.removeEventListener('keydown', this._keydownHandler);
        this._keydownHandler = null;
      }
    }

    /**
     * Handles input changes by validating the input field and updating the character counter.
     * @param {Event} e - The input event.
     */
    handleInputChange(e) {
      const target = e.target;
      if (target.matches('[data-engraving-input]')) {
        // 1. Live Validation Feedback
        this.validateInputField(target);

        // 2. Update character counter
        const wrapper = target.closest('.engraving-input__wrapper');
        if (wrapper) {
          const counter = wrapper.querySelector('.engraving-input__counter');
          if (counter) {
            counter.textContent = `${target.value.length} / ${target.maxLength}`;
          }
        }

        // 3. Update 3D viewer only for the active position's inputs
        if (this.isInputForActiveTemplate(target)) {
          this.syncViewerTextFromActivePosition();
          this.scheduleViewerPositionTurn();
        }
      }

      // Position change: move viewer, switch sku/text area, and restore position text
      if (
        target.getAttribute('name').includes('[__Engraving-position]') &&
        target.checked
      ) {
        this.currentPosition = target.value;
        this.resetPositionTurnCooldown();
        this.update3DViewerPosition(target.value);
        this.update3DViewerSku3d();
        this.syncViewerTextForPosition(target.value);

        /** @type {HTMLInputElement | null} */
        const fontInput = this.querySelector('input[name*="Font"]:checked');
        if (fontInput) {
          this.update3DViewerFont(fontInput.value);
        }
      }

      // Handle font change
      if (target.getAttribute('name').includes('Font') && target.checked) {
        this.update3DViewerFont(target.value);
      }

      // Handle color change
      if (target.getAttribute('name').includes('JigText') && target.checked) {
        this.update3DViewerColor(target.value);
      }

      this.syncSelectedFontSizeInputs()
      this.validateAll();
    }

    syncSelectedFontSizeInputs() {
      /** @type {HTMLInputElement[]} */
      const fontSizeInputs = Array.from(
        this.querySelectorAll('[data-engraving-font-size-input]')
      )

      fontSizeInputs.forEach((fontSizeInput) => {
        const option = fontSizeInput.closest('.engraving-option')
        const radioInput = option?.querySelector('input[type="radio"]')

        if (!(radioInput instanceof HTMLInputElement)) {
          fontSizeInput.disabled = true
          return
        }

        fontSizeInput.disabled = !radioInput.checked
      })
    }

    /**
     * Handles click events on the panel.
     * @param {Event} e - The click event.
     */
    handleClick(e) {
      const target = e.target;

      if (target.matches('.engraving-tabs__item input[data-tab-id]')) {
        const index = this.tabInputs.indexOf(target);
        if (index > 0) {
          for (let i = 0; i < index; i++) {
            if (!this.validateStep(i + 1)) {
              e.preventDefault();
              this.showStepErrors(i + 1);
              return;
            }
          }
        }
        this.setNavigationDirection(
          index > this.currentTabIndex ? 'forward' : 'backward',
        );
        this.currentTabIndex = index;
        this.focusFirstInStep(index);
      }

      const label = target.closest('label[for]');
      if (label) {
        const targetId = label.getAttribute('for');
        const targetInput = this.querySelector(`input[id="${targetId}"]`);
        if (targetInput && targetInput.hasAttribute('data-tab-id')) {
          const targetIndex = this.tabInputs.indexOf(targetInput);
          const currentPanel = label.closest('.bottom-panel');
          const currentStepIndex = this.footerPanels.indexOf(currentPanel);

          if (targetIndex > currentStepIndex) {
            if (!this.validateStep(currentStepIndex + 1)) {
              e.preventDefault();
              this.showStepErrors(currentStepIndex + 1);
              return;
            }
          }
          this.setNavigationDirection(
            targetIndex > this.currentTabIndex ? 'forward' : 'backward',
          );
          this.currentTabIndex = targetIndex;
          this.focusFirstInStep(targetIndex);
        }
      }

      if (target.closest('[data-engraving-close]')) {
        this.close();
      }

      if (target.closest('button[type="submit"]')) {
        let isFormValid = true;
        for (let i = 1; i <= this.footerPanels.length; i++) {
          if (!this.validateStep(i)) {
            isFormValid = false;
            this.showStepErrors(i);
          }
        }

        if (!isFormValid) {
          e.preventDefault();
        }
      }
    }

    /**
     * Sets slide animation direction on the panel (mobile tab transitions).
     * @param {'forward' | 'backward'} direction
     */
    setNavigationDirection(direction) {
      this.dataset.direction = direction;
      clearTimeout(this._directionTimeout);
      this._directionTimeout = setTimeout(() => {
        delete this.dataset.direction;
      }, 400);
    }

    /**
     * Detects Japanese scripts (hiragana, katakana, kanji).
     * @param {string} value
     * @returns {boolean}
     */
    hasJapaneseCharacters(value) {
      try {
        return /\p{Script=Hiragana}|\p{Script=Katakana}|\p{Script=Han}/u.test(
          value,
        );
      } catch {
        return /[\u3040-\u309F\u30A0-\u30FF\u4E00-\u9FFF]/u.test(value);
      }
    }

    /**
     * Returns a specific error message for invalid characters, or null if valid.
     * @param {string} value
     * @param {RegExp} allowedRegex
     * @returns {string | null}
     */
    getCharacterValidationError(value, allowedRegex) {
      const fallback =
        this.dataset.errorUnauthorized || 'Contains unauthorized symbols.';

      if (
        !/[\u{FF00}-\u{FFEF}]/u.test(value) &&
        allowedRegex.test(value)
      ) {
        return null;
      }

      if (this.hasJapaneseCharacters(value)) {
        return this.dataset.errorJapanese || fallback;
      }

      if (/[\u{FF00}-\u{FFEF}]/u.test(value)) {
        return this.dataset.errorFullwidth || fallback;
      }

      return fallback;
    }

    /**
     * Builds a regex matching only allowed engraving characters (Latin half-width).
     * @param {string} extraSymbols - Escaped punctuation/symbols appended to the base charset.
     * @returns {RegExp}
     */
    buildAllowedRegex(extraSymbols) {
      const allowedCharsRegexPart = `\\s\\p{Script=Latin}0-9${extraSymbols}`;
      const clsModern = `^[${allowedCharsRegexPart}]+$`;
      const clsFallback = `^[\\sA-Za-zÀ-ÖØ-öø-ÿ0-9${extraSymbols}]+$`;
      try {
        return new RegExp(clsModern, 'u');
      } catch {
        return new RegExp(clsFallback, '');
      }
    }

    /**
     * Validates an input field and updates its error state.
     * @param {HTMLInputElement} input - The input element to validate.
     * @returns {boolean} - True if the input is valid, false otherwise.
     */
    validateInputField(input) {
      const val = input.value.trim();

      if (val === '') {
        this.clearError(input);
        return true;
      }

      const valLower = val.toLowerCase();

      // 1. Forbidden symbols check — Latin half-width letters and digits only
      let extraSymbols = '';

      const isMetafieldProvided = this.hasAttribute('data-list-allowed-symbol');

      if (isMetafieldProvided) {
        if (this.allowedSymbols.length > 0) {
          extraSymbols = this.allowedSymbols
            .map((s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
            .join('');
        }
      } else {
        extraSymbols = '!?. ,&()\\-\'"℻';
      }

      const allowedRegex = this.buildAllowedRegex(extraSymbols);
      const charError = this.getCharacterValidationError(
        input.value,
        allowedRegex,
      );

      if (charError) {
        this.setError(input, charError);
        return false;
      }

      // 2. Forbidden words check
      const hasForbiddenWord = this.forbiddenWords.some((word) =>
        valLower.includes(word.toLowerCase()),
      );
      if (hasForbiddenWord) {
        this.setError(
          input,
          this.dataset.errorForbidden || 'Contains forbidden words.',
        );
        return false;
      }

      this.clearError(input);
      return true;
    }
    /**
     * Validates a specific step in the engraving panel.
     * @param {number} stepNumber - The step number to validate.
     * @returns {boolean} - True if the step is valid, false otherwise.
     */
    validateStep(stepNumber) {
      if (stepNumber === 1) return true;

      if (stepNumber === 2) {
        const activeTemplateInput = this.querySelector(
          'input[data-template-nbr]:checked',
        );
        if (!activeTemplateInput) return false;

        const templateNbr = activeTemplateInput.dataset.templateNbr;
        const inputs = Array.from(
          this.querySelectorAll(
            `.engraving-step__content-${templateNbr} [data-engraving-input]`,
          ),
        );

        if (inputs.length === 0) return true;

        const isOneFilled = inputs.some(
          (input) => input.value.trim().length > 0,
        );
        if (!isOneFilled) return false;

        // Use the granular validation for each input
        return inputs.every((input) => this.validateInputField(input));
      }

      return true;
    }

    /**
     * Shows error messages for a specific step in the engraving panel.
     * @param {number} stepNumber - The step number to show errors for.
     */
    showStepErrors(stepNumber) {
      if (stepNumber === 2) {
        const activeTemplateInput = this.querySelector(
          'input[data-template-nbr]:checked',
        );
        if (!activeTemplateInput) return;

        const templateNbr = activeTemplateInput.dataset.templateNbr;
        const inputs = Array.from(
          this.querySelectorAll(
            `.engraving-step__content-${templateNbr} [data-engraving-input]`,
          ),
        );

        const isOneFilled = inputs.some(
          (input) => input.value.trim().length > 0,
        );

        if (!isOneFilled) {
          inputs.forEach((input) => {
            this.setError(
              input,
              this.dataset.errorMissingLine ||
                'Please fill in at least one line.',
            );
          });
        } else {
          inputs.forEach((input) => this.validateInputField(input));
        }
      }
    }

    setError(input, message) {
      const errorDiv = document.getElementById(`${input.id}-error`);
      if (!errorDiv) return;
      const errorText = errorDiv.querySelector('[data-error-text]');
      if (errorText) {
        errorText.textContent = message;
      } else {
        errorDiv.textContent = message;
      }
      errorDiv.classList.add('is-active');
      input.classList.add('is-error');
    }

    clearError(input) {
      const errorDiv = document.getElementById(`${input.id}-error`);
      if (!errorDiv) return;
      const errorText = errorDiv.querySelector('[data-error-text]');
      if (errorText) {
        errorText.textContent = '';
      } else {
        errorDiv.textContent = '';
      }
      errorDiv.classList.remove('is-active');
      input.classList.remove('is-error');
    }

    validateAll() {
      this.updateTabsState();
      this.updateNavigationButtons();
    }

    updateTabsState() {
      this.tabInputs.forEach((input, index) => {
        const stepNumber = index + 1;
        let isAccessible = true;

        if (stepNumber > 1) {
          for (let i = 1; i < stepNumber; i++) {
            if (!this.validateStep(i)) {
              isAccessible = false;
              break;
            }
          }
        }

        const tabItem = input.closest('.engraving-tabs__item');
        if (tabItem) {
          tabItem.classList.toggle('is-disabled', !isAccessible);
        }
      });
    }

    updateNavigationButtons() {
      let isAllStepsValid = true;

      this.footerPanels.forEach((panel, index) => {
        const stepNumber = index + 1;
        const isCurrentStepValid = this.validateStep(stepNumber);
        if (!isCurrentStepValid) isAllStepsValid = false;

        const navElements = panel.querySelectorAll(
          'label[for], button[type="submit"]',
        );

        navElements.forEach((el) => {
          if (el.tagName === 'LABEL') {
            const targetId = el.getAttribute('for');
            const targetInput = this.querySelector(`input[id="${targetId}"]`);
            if (targetInput) {
              const targetIndex = this.tabInputs.indexOf(targetInput);
              if (targetIndex < index) {
                el.classList.remove('button--disabled');
                return;
              }
            }
          }

          el.classList.toggle('button--disabled', !isCurrentStepValid);
        });
      });

      // Handle Desktop Submit Button
      if (this.desktopSubmitButton) {
        this.desktopSubmitButton.classList.toggle(
          'button--disabled',
          !isAllStepsValid,
        );
      }
    }

    /**
     * Reads the currently-checked radios for position, font, and color,
     * then pushes their values to the 3D viewer so it reflects defaults on open.
     */
    syncDefaultValues() {
      const selectedPosition = this.getSelectedEngravingPosition();
      if (selectedPosition) {
        this.currentPosition = selectedPosition;
      }

      const activePosition = this.getSelectedEngravingPosition();
      if (activePosition) {
        this.update3DViewerPosition(activePosition);
      } else {
        this.applyViewerPositionIfNeeded();
      }

      this.update3DViewerSku3d();
      this.syncViewerTextFromActivePosition();

      /** @type {HTMLInputElement | null} */
      const fontInput = this.querySelector('input[name*="Font"]:checked');
      if (fontInput) {
        this.update3DViewerFont(fontInput.value);
      }

      /** @type {HTMLInputElement | null} */
      const colorInput = this.querySelector('input[name*="JigText"]:checked');
      if (colorInput) {
        this.update3DViewerColor(colorInput.value);
      }
    }

    // 3D Viewer functions
    update3DViewerSku3d() {
      const viewer = this.querySelector('power-viewer-360-3d');
      const { sku3d } = this.getActiveTemplateViewerConfig();

      if (!viewer || !sku3d) return;

      viewer.setAttribute('sku-3d', sku3d);
      viewer.setAttribute('data-sku-3d', sku3d);
    }

    update3DViewerPosition(position) {
      const config = this.getPositionConfig(position);
      if (!config) return;

      this.currentPosition = position;
      const viewer = this.querySelector('power-viewer-360-3d');
      if (!viewer) return;


      viewer.removeAttribute('focus-view');
      viewer.setAttribute('focus-view', config.focusView);
      viewer.setAttribute('text-area', config.textArea);
    }

    /**
     * @param {string} font
     */
    buildViewerFontAttribute(font) {
      const { diptyquePx, desmondPx } = this.getActiveTemplateViewerConfig();

      if (font.includes('DIPTYQUE')) {
        return `500 ${diptyquePx}px Diptyque Saint Germain, sans-serif`;
      }

      if (font.includes('DESMOND')) {
        return `200 ${desmondPx}px Desmond Handwriting, sans-serif`;
      }

      return font;
    }

    update3DViewerFont(font) {
      const viewer = this.querySelector('power-viewer-360-3d');
      if (viewer) {
        viewer.setAttribute('text-font', this.buildViewerFontAttribute(font));
      }
    }

    update3DViewerColor(color) {
      const viewer = this.querySelector('power-viewer-360-3d');
      if (viewer) {
        if (color == 'Gold') {
          viewer.setAttribute('color', 'gold');
        } else if (color == 'Silver') {
          viewer.setAttribute('color', 'silver');
        }
      }
    }
  }

  customElements.define('c-engraving-panel', CEngravingPanel);
}
